/**
 * B663 — the fleet's ONE calendar register, US order (the B649 rule: the
 * product speaks US English). Every web surface, email and PDF that names a
 * day calls `calendarWords`; the shape lives here so a grep wall can keep it
 * that way (packages/domain/test/b663CalendarWords.test.ts).
 *
 *   long      "Friday, October 2"
 *   short     "Fri, Oct 2"        (the default)
 *   monthDay  "Oct 2"
 *   + year    "Fri, Oct 2, 2026"
 *
 * Composed from en-US parts, so neither the viewer's locale nor a runtime's
 * CLDR ("Sept") can reshape it. The zone is the TENANT's — a day word in the
 * wrong zone is a wrong fact. The clock half is `clockWords`' (B621).
 */
export type CalendarStyle = "long" | "short" | "monthDay";

export interface CalendarOptions {
  /** The tenant's zone; absent = the runtime's (callers with a calendar date pass "UTC"). */
  readonly timeZone?: string;
  readonly style?: CalendarStyle;
  readonly year?: boolean;
}

export function calendarWords(ms: number, opts: CalendarOptions = {}): string {
  const style = opts.style ?? "short";
  const width = style === "long" ? "long" : "short";
  const parts = new Intl.DateTimeFormat("en-US", {
    ...(opts.timeZone !== undefined ? { timeZone: opts.timeZone } : {}),
    ...(style === "monthDay" ? {} : { weekday: width }),
    month: width,
    day: "numeric",
    year: "numeric",
  }).formatToParts(ms);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? "";
  const month = width === "short" && get("month") === "Sept" ? "Sep" : get("month");
  const monthDay = `${month} ${get("day")}`;
  const head = style === "monthDay" ? monthDay : `${get("weekday")}, ${monthDay}`;
  return opts.year === true ? `${head}, ${get("year")}` : head;
}
